import React from 'react';
import PropTypes from 'prop-types';
import { usePermissions } from 'context/PermissionContext';

/**
 * ProtectedComponent - Conditionally renders children based on screen permissions
 * 
 * @param {string} screenCode - The screen code to check permissions for
 * @param {string} permission - The permission type to check ('access', 'view', 'add', 'edit', 'delete')
 * @param {React.ReactNode} children - The content to render if permission is granted
 * @param {React.ReactNode} fallback - Optional fallback content to render if permission is denied
 * @param {boolean} requireAll - If true, requires all permissions to be true (for multiple permissions)
 * @param {Array<string>} permissions - Array of permissions to check (used with requireAll)
 */
const ProtectedComponent = ({
  screenCode,
  permission = 'access',
  children,
  fallback = null,
  requireAll = false,
  permissions = null,
}) => {
  const { checkPermission, hasAccess, canView, canAdd, canEdit, canDelete, loading } = usePermissions();

  // If still loading permissions, don't render anything (or show loading state)
  if (loading) {
    return null;
  }

  // If screenCode is null or empty, allow access (for cards without screen codes)
  if (!screenCode) {
    return <>{children}</>;
  }

  let hasPermission = false;

  // If permissions array is provided, check all of them
  if (permissions && Array.isArray(permissions)) {
    if (requireAll) {
      // All permissions must be true
      hasPermission = permissions.every((perm) => {
        switch (perm) {
          case 'access':
            return hasAccess(screenCode);
          case 'view':
            return canView(screenCode);
          case 'add':
            return canAdd(screenCode);
          case 'edit':
            return canEdit(screenCode);
          case 'delete':
            return canDelete(screenCode);
          default:
            return checkPermission(screenCode, perm);
        }
      });
    } else {
      // At least one permission must be true
      hasPermission = permissions.some((perm) => {
        switch (perm) {
          case 'access':
            return hasAccess(screenCode);
          case 'view':
            return canView(screenCode);
          case 'add':
            return canAdd(screenCode);
          case 'edit':
            return canEdit(screenCode);
          case 'delete':
            return canDelete(screenCode);
          default:
            return checkPermission(screenCode, perm);
        }
      });
    }
  } else {
    // Single permission check
    switch (permission) {
      case 'access':
        hasPermission = hasAccess(screenCode);
        break;
      case 'view':
        hasPermission = canView(screenCode);
        break;
      case 'add':
        hasPermission = canAdd(screenCode);
        break;
      case 'edit':
        hasPermission = canEdit(screenCode);
        break;
      case 'delete':
        hasPermission = canDelete(screenCode);
        break;
      default:
        hasPermission = checkPermission(screenCode, permission);
    }
  }

  return hasPermission ? <>{children}</> : <>{fallback}</>;
};

ProtectedComponent.propTypes = {
  screenCode: PropTypes.string,
  permission: PropTypes.string,
  children: PropTypes.node,
  fallback: PropTypes.node,
  requireAll: PropTypes.bool,
  permissions: PropTypes.arrayOf(PropTypes.string),
};

ProtectedComponent.defaultProps = {
  permission: 'access',
  children: null,
  fallback: null,
  requireAll: false,
  permissions: null,
};

export default ProtectedComponent;

